import { Component, forwardRef, Input, ViewChild } from '@angular/core';
import { PlatformApiService } from '../../services/platform-api.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Account, RoleType} from '../../models/AccountModel';
import { WizardScreenComponent } from 'src/app/shared/wizard.component';
import { AutomateClientDropdownComponent } from 'src/app/shared/automate-client-dropdown';
import { InfiniteScrollOption } from 'src/app/shared/infinite-scroll-select.component';
import { SessionService } from 'src/app/services/session.service';

@Component({
  selector: 'cw-ci-account-automate-integration',
  templateUrl: './account-automate-integration.component.html',
  styleUrls: ['./account-automate-integration.component.scss'],
  providers: [{provide: WizardScreenComponent, useExisting: forwardRef(() => AccountAutomateIntegrationComponent)}]
})
export class AccountAutomateIntegrationComponent extends WizardScreenComponent {
  @Input() account: Account;
  @ViewChild(AutomateClientDropdownComponent) dropdown: AutomateClientDropdownComponent;
  isNewIntegration = true;
  RoleType = RoleType;
  public verifyForm: FormGroup;

  set readOnly(val: boolean) {
    super.readOnly = val;
    if(val) {
      this.verifyForm.disable();
    } else {
      this.verifyForm.enable();
    }
  }

  constructor(private _apiService: PlatformApiService, public sessionService: SessionService) {
    super();
    this.verifyForm = new FormGroup ({
      companySite: new FormControl('', [Validators.required]),
      username: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
    });

    this.form = new FormGroup ({
      enableAutomate: new FormControl(),
      clientId: new FormControl('', [Validators.required]),
    });
  }

  onLoad() {
    this.showSpinner()
    this.form.reset();
    this._apiService.getAutomateIntegration(this.account.id).subscribe(data => {
      if (data) {
        this.isNewIntegration = false;
        this.verifyForm.controls.companySite.setValue(data.companySite);
        this.verifyForm.controls.username.setValue(data.username);
        this.verifyForm.controls.password.setValue('**redacted**');
        this.form.controls.enableAutomate.setValue(data.isEnabled);
        if (data.clientId && data.clientName) {
          this.form.controls.clientId.setValue(new InfiniteScrollOption(data.clientName, data.clientId, false, false, true));
        }

        this.onEnableChange();
      } else {
        this.verifyForm.controls.password.setValidators([Validators.required]);
      }

      this.verifyForm.controls.companySite.updateValueAndValidity();
      this.verifyForm.controls.username.updateValueAndValidity();
      this.verifyForm.controls.password.updateValueAndValidity();
      this.form.controls.enableAutomate.updateValueAndValidity();
      this.form.controls.clientId.updateValueAndValidity();

      this.verifyForm.markAsPristine();
      this.form.markAsPristine();

      this.hideSpinner();
    }, err => {
      this.hideSpinner();
      super.onLoad();
    });
  }

  verifyIntegration() {
    if (this.verifyForm.valid) {
      this.showSpinner('Verifying...');

      let calculatedDomain = this.verifyForm.value.companySite;

      if (calculatedDomain) {
        calculatedDomain = calculatedDomain.toLowerCase();
        let domainArray = calculatedDomain.split('://');
        if (domainArray.length > 1) {
          calculatedDomain = domainArray[1];
        }

        domainArray = calculatedDomain.split('/');
        if (domainArray.length > 1) {
          calculatedDomain = domainArray[0];
        }

        if (calculatedDomain.startsWith('api-')) {
          calculatedDomain = calculatedDomain.replace('api-', '');
        }
      } else {

      }

      const integrationObj = {
        companySite: calculatedDomain,
        username: this.verifyForm.value.username,
        password: undefined
      };

      if (this.verifyForm.value.password != '**redacted**') {
        integrationObj.password = this.verifyForm.value.password
      }

      this._apiService.saveAutomateIntegration(this.account.id, integrationObj).subscribe(resp => {
        if (this.isNewIntegration) {
          this.showMessage('Integration setup has been saved. Please select the Automate client that represents your company.');
        }
        this.onLoad();
      }, err => {
        this.verifyForm.markAllAsTouched();
        if (err.status == 401) {
          this.verifyForm.setErrors({failedAuth: true});
        } else if (err.status == 403) {
          this.verifyForm.controls.username.setErrors({invalidPermissions: true});
        } else if (err.error) {
          if (err.error.code == 'MFA_REQUIRED') {
            this.verifyForm.controls.username.setErrors({mfaRequired: true});
          } else if (err.error.code == 'CONNECTWISE_BAD_SITE') {
            this.verifyForm.controls.companySite.setErrors({connectionError: true});
          } else if (err.error?.error?.statusCode == 423) {
            this.verifyForm.controls.username.setErrors({accountLocked:true})
          } else {
            this.showMessage('An error occurred while saving. Please contact an administrator.');
          }
        }
        this.hideSpinner();
      });
    }
  }

  saveIntegration() {
    if (this.verifyForm.dirty && this.verifyForm.valid) {
      this.verifyIntegration();
    } else if (this.form.valid) {
      this.showSpinner('Saving...');

      const companySite = this.verifyForm.value.companySite;
      const clientId = (this.form.value.clientId != undefined) ? this.form.value.clientId.value : undefined;
      const clientName = (this.form.value.clientId != undefined) ? this.form.value.clientId.description : undefined;

      const integrationObj = {
        isEnabled: this.form.value.enableAutomate,
        companySite,
        clientId,
        clientName,
        username: this.verifyForm.value.username,
        password: undefined
      };

      if (this.verifyForm.value.password != '**redacted**') {
        integrationObj.password = this.verifyForm.value.password
      }

      this._apiService.saveAutomateIntegration(this.account.id, integrationObj).subscribe(resp => {
        this.onLoad();
      }, err => {
        if (err.status == 401) {
          this.verifyForm.controls.username.setErrors({invalid: true});
        } else if (err.status == 403) {
          this.verifyForm.controls.username.setErrors({invalidPermissions: true});
        } else if (err.error) {
          console.log(err.error);
          if (err.error.code == 'MFA_REQUIRED') {
            this.verifyForm.controls.username.setErrors({mfaRequired: true});
          } else if (err.error.code == 'CONNECTWISE_BAD_SITE') {
            this.verifyForm.controls.companySite.setErrors({connectionError: true});
          } else if (err.error.code == 'CONNECTWISE_BAD_CLIENTID') {
            this.form.controls.clientId.setErrors({invalid: true});
          } else if (err.error.code == 'INTEGRATION_ALREADY_EXISTS') {
            this.form.controls.clientId.setErrors({alreadyExists: true});
          } else {
            this.showMessage('An error occurred while saving. Please contact an administrator.');
          }
        }
        this.form.setErrors({saveError: true});
        this.hideSpinner();
      });
    }
  }

  onEnableChange() {
    if (this.form.controls.enableAutomate.value) {
      this.dropdown.required = true;
      this.form.controls.clientId.setValidators([Validators.required]);
    } else {
      this.dropdown.required = false;
      this.form.controls.clientId.setValidators([]);
    }

    this.form.controls.clientId.updateValueAndValidity();
  }

  onSave() {
    this.saveIntegration();
    super.onSave();
  }

  handleEvent(event) {
    if(event.state == 'ONSELECT') {
      this.form.controls.enableAutomate.setValue(true);
    }
  }
}